import {
  MessageCircle,
  Handshake,
  Heart,
  PhoneCall,
  PartyPopper,
  Sprout,
  Smartphone,
  CalendarDays,
  Target,
  Mail,
  Flame,
  Headset,
  LineChart,
  ArrowDown,
  Sparkles,
  Users,
  ShieldCheck,
  Rocket,
  LucideIcon,
} from "lucide-react";

interface Duty {
  Icon: LucideIcon;
  text: string;
}

const AFFILIATE_DUTIES: Duty[] = [
  { Icon: MessageCircle, text: "Reply to email inquiries and messages from qualified leads" },
  { Icon: Handshake, text: "Build trust and genuine relationships with your prospects" },
  { Icon: Heart, text: "Nurture leads before and after Discovery Calls" },
  { Icon: PhoneCall, text: "Continue conversations when prospects have additional questions" },
  { Icon: PartyPopper, text: "Welcome and support your new subscribers" },
  { Icon: Sprout, text: "Continue serving your growing subscriber community" },
];

const WINCAMPAIGN_DUTIES: Duty[] = [
  { Icon: Smartphone, text: "Create your monthly social media content" },
  { Icon: CalendarDays, text: "Schedule your content for consistent visibility" },
  { Icon: Target, text: "Research qualified prospects" },
  { Icon: Mail, text: "Conduct personalized outreach campaigns" },
  { Icon: Flame, text: "Deliver qualified leads to your pipeline" },
  { Icon: Headset, text: "Schedule and conduct up to 10 Discovery Calls per month (ELITE)" },
  { Icon: LineChart, text: "Provide monthly Growth Reviews and recommendations" },
];

interface FlowStep {
  title: string;
  owner: "wincampaign" | "affiliate" | "lead" | "outcome";
  Icon: LucideIcon;
}

const FLOW: FlowStep[] = [
  { title: "WinCampaign creates visibility", owner: "wincampaign", Icon: Smartphone },
  { title: "Qualified leads generated", owner: "wincampaign", Icon: Flame },
  { title: "Brand Affiliate builds the relationship", owner: "affiliate", Icon: MessageCircle },
  { title: "Discovery Call scheduled", owner: "lead", Icon: CalendarDays },
  {
    title: "WinCampaign Growth Advisor conducts the Discovery Call (ELITE)",
    owner: "wincampaign",
    Icon: Headset,
  },
  { title: "New subscriber", owner: "lead", Icon: PartyPopper },
  {
    title: "Brand Affiliate continues serving & growing the relationship",
    owner: "affiliate",
    Icon: Heart,
  },
  { title: "Monthly recurring income", owner: "outcome", Icon: Rocket },
];

const OWNER_STYLES: Record<
  FlowStep["owner"],
  { label: string; tileBg: string; tileFg: string; badgeBg: string; badgeFg: string }
> = {
  wincampaign: {
    label: "WinCampaign",
    tileBg: "bg-[#d3fc3a]",
    tileFg: "text-[#1a1a24]",
    badgeBg: "bg-[#d3fc3a]",
    badgeFg: "text-[#1a1a24]",
  },
  affiliate: {
    label: "You",
    tileBg: "bg-[#673dfc]",
    tileFg: "text-white",
    badgeBg: "bg-[#673dfc]",
    badgeFg: "text-white",
  },
  lead: {
    label: "Lead",
    tileBg: "bg-gray-100",
    tileFg: "text-gray-500",
    badgeBg: "bg-gray-100",
    badgeFg: "text-gray-600",
  },
  outcome: {
    label: "Outcome",
    tileBg: "bg-[#30265b]",
    tileFg: "text-[#d3fc3a]",
    badgeBg: "bg-[#30265b]",
    badgeFg: "text-[#d3fc3a]",
  },
};

export default function HowWeWorkTogether() {
  return (
    <section
      id="partnership"
      data-testid="wc-partnership"
      className="bg-white py-24 sm:py-32"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-[#f3efff] text-[#673dfc] font-nav font-semibold text-xs rounded-full px-3 py-1 mb-4">
            <Sparkles size={13} /> Our partnership
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-5xl lg:text-[52px] tracking-tight text-[#30265b] leading-[1.05]">
            We&apos;re your growth partner —{" "}
            <span className="text-[#673dfc]">not a replacement for you.</span>
          </h2>
          <div className="mt-6 space-y-4 text-lg text-gray-700 leading-relaxed">
            <p>
              The most successful WinCampaign Brand Affiliates don&apos;t build
              their business alone. But they also don&apos;t expect someone
              else to build it for them.
            </p>
            <p>
              WinCampaign becomes an extension of your business by handling
              the time-consuming marketing and lead generation activities,
              while you continue building relationships, serving people, and
              growing your recurring income.
            </p>
            <p className="font-display font-semibold text-[#30265b]">
              Together, we each focus on what we do best.
            </p>
          </div>
        </div>

        {/* Two-column */}
        <div className="mt-14 sm:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Brand Affiliate column */}
          <div
            data-testid="wc-partnership-you"
            className="rounded-3xl bg-white border-2 border-[#673dfc]/20 p-8 sm:p-10 hover:border-[#673dfc]/60 hover:shadow-xl hover:shadow-[#673dfc]/10 transition-all duration-500"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#673dfc] text-white grid place-items-center">
                <Users size={22} strokeWidth={1.75} />
              </div>
              <div className="text-xs font-nav font-semibold uppercase tracking-[0.14em] text-[#673dfc]">
                You · Brand Affiliate
              </div>
            </div>
            <h3 className="mt-5 font-display font-bold text-2xl sm:text-3xl text-[#30265b] leading-tight">
              Your role as a Brand Affiliate
            </h3>
            <p className="mt-3 text-base text-gray-700 leading-relaxed">
              You remain the trusted face of your business. Your
              responsibilities include:
            </p>
            <ul className="mt-6 space-y-3">
              {AFFILIATE_DUTIES.map((d) => {
                const Icon = d.Icon;
                return (
                  <li key={d.text} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-xl bg-[#f3efff] text-[#673dfc] grid place-items-center flex-shrink-0">
                      <Icon size={15} strokeWidth={1.75} />
                    </div>
                    <span className="text-sm sm:text-[15px] text-gray-700 leading-relaxed pt-1.5">
                      {d.text}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-7 rounded-2xl bg-[#f8f6ff] border border-[#c5b4ff]/50 p-4">
              <div className="text-[11px] font-nav font-semibold uppercase tracking-[0.14em] text-[#673dfc] mb-1">
                Your superpower
              </div>
              <p className="text-sm text-[#30265b] font-display font-medium leading-relaxed">
                Building relationships that create long-term retention and
                recurring income.
              </p>
            </div>
          </div>

          {/* WinCampaign column */}
          <div
            data-testid="wc-partnership-us"
            className="rounded-3xl bg-[#30265b] text-white border-2 border-[#d3fc3a]/30 p-8 sm:p-10 hover:border-[#d3fc3a]/70 hover:shadow-2xl hover:shadow-[#d3fc3a]/15 transition-all duration-500"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#d3fc3a] text-[#1a1a24] grid place-items-center">
                <ShieldCheck size={22} strokeWidth={1.75} />
              </div>
              <div className="text-xs font-nav font-semibold uppercase tracking-[0.14em] text-[#d3fc3a]">
                WinCampaign · Your team
              </div>
            </div>
            <h3 className="mt-5 font-display font-bold text-2xl sm:text-3xl leading-tight">
              What WinCampaign handles
            </h3>
            <p className="mt-3 text-base text-white/80 leading-relaxed">
              We help remove the biggest bottlenecks that slow business
              growth. WinCampaign will:
            </p>
            <ul className="mt-6 space-y-3">
              {WINCAMPAIGN_DUTIES.map((d) => {
                const Icon = d.Icon;
                return (
                  <li key={d.text} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-xl bg-[#d3fc3a] text-[#1a1a24] grid place-items-center flex-shrink-0">
                      <Icon size={15} strokeWidth={1.75} />
                    </div>
                    <span className="text-sm sm:text-[15px] text-white/85 leading-relaxed pt-1.5">
                      {d.text}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-7 rounded-2xl bg-[#d3fc3a] text-[#1a1a24] p-4">
              <div className="text-[11px] font-nav font-semibold uppercase tracking-[0.14em] text-[#1a1a24]/70 mb-1">
                Our superpower
              </div>
              <p className="text-sm font-display font-medium leading-relaxed">
                Helping you consistently attract and convert more qualified
                prospects into subscribers.
              </p>
            </div>
          </div>
        </div>

        {/* Visual workflow */}
        <div className="mt-16 sm:mt-20">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-[#f3efff] text-[#673dfc] font-nav font-semibold text-xs rounded-full px-3 py-1 mb-4">
              How growth happens
            </div>
            <h3 className="font-display font-bold text-2xl sm:text-3xl text-[#30265b] leading-tight">
              The path from qualified lead to recurring income.
            </h3>
          </div>

          {/* Legend */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-xs font-nav font-semibold">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#673dfc] text-white px-3 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-white" /> You
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#d3fc3a] text-[#1a1a24] px-3 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1a1a24]" /> WinCampaign
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 text-gray-600 px-3 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-500" /> Lead
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#30265b] text-[#d3fc3a] px-3 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#d3fc3a]" /> Outcome
            </span>
          </div>

          <ol className="mt-10 max-w-2xl mx-auto space-y-3">
            {FLOW.map((s, i) => {
              const meta = OWNER_STYLES[s.owner];
              const Icon = s.Icon;
              return (
                <div key={i}>
                  <li
                    data-testid={`wc-partnership-flow-${i + 1}`}
                    className="rounded-3xl border border-gray-100 bg-white px-5 py-4 flex items-center gap-4 hover:border-[#c5b4ff] hover:shadow-md transition-all"
                  >
                    <div className={`w-11 h-11 rounded-2xl grid place-items-center flex-shrink-0 ${meta.tileBg} ${meta.tileFg}`}>
                      <Icon size={20} strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-[10px] font-nav font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full inline-flex ${meta.badgeBg} ${meta.badgeFg} mb-1`}>
                        {meta.label}
                      </div>
                      <div className="font-display font-bold text-[#30265b] leading-snug">
                        {s.title}
                      </div>
                    </div>
                  </li>
                  {i < FLOW.length - 1 && (
                    <div className="flex justify-center py-1.5">
                      <ArrowDown size={16} className="text-[#c5b4ff]" strokeWidth={2.5} />
                    </div>
                  )}
                </div>
              );
            })}
          </ol>

          <p className="mt-10 text-center text-sm text-gray-600 max-w-2xl mx-auto leading-relaxed">
            WinCampaign is not replacing you. WinCampaign becomes an extension
            of your business — removing the most time-consuming work so you
            can spend more time building relationships and growing your
            recurring income.
          </p>
        </div>
      </div>
    </section>
  );
}
